/**
 * BillingPage.jsx
 * ===============
 * Billing and Usage management page
 * 
 * Created: 2025-01-27
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ProfileIcon, ProfileMenu, ProfileMenuItem } from '../components/Header/ProfileMenu';
import { FaUserCircle } from 'react-icons/fa';
import { IoSettingsOutline } from 'react-icons/io5';
import { FiLogOut } from 'react-icons/fi';
import Logout from '../components/Logout/Logut';
import useProfileStore from '../store/useProfileStore';
import logo from '../assets/images/newLogo.png';

const BillingPage = () => {
  const navigate = useNavigate();
  const userImage = useProfileStore((state) => state.userImage);

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: '#F9FAFB'
    }}>
      {/* Custom Navigation Bar */}
      <div style={{
        padding: '12px 24px',
        background: '#050618',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        minHeight: '5.25rem',
        position: 'sticky',
        top: 0,
        zIndex: 1000
      }}>
        {/* Left Section: MUDUMBAI Logo */}
        <div 
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            marginRight: '32px'
          }}
          onClick={() => navigate('/')}
        >
          <img 
            src={logo} 
            alt="MUDUMBAI Logo" 
            style={{
              height: '3.5rem',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* Center Section: Page-specific content */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flex: 1
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px'
            }}>
              💳
            </div>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: '700',
                color: '#FFFFFF',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
              }}>
                Billing & Usage
              </h1>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: 'rgba(255, 255, 255, 0.8)'
              }}>
                Manage your subscription, view usage limits, and access invoices
              </p>
            </div>
          </div>
        </div>

        {/* Right Section: Go to Dashboard and Profile */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {/* Go to Dashboard Button */}
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '8px 14px',
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.3)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            <span>🏠</span>
            <span>Go to Dashboard</span>
          </button>

          {/* Profile Icon */}
          <div style={{ marginLeft: '12px' }}>
            <ProfileMenu
              trigger={
                <div style={{
                  width: '2.75rem',
                  height: '2.75rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                }}
                >
                  <ProfileIcon userImage={userImage} />
                </div>
              }
            >
              <ProfileMenuItem onClick={() => navigate('/userprofile')}>
                <FaUserCircle size={22} /> Profile
              </ProfileMenuItem>
              <ProfileMenuItem onClick={() => navigate('/settings')}>
                <IoSettingsOutline size={22} /> Setting
              </ProfileMenuItem>
              <ProfileMenuItem>
                <FiLogOut size={20} /> <Logout />
              </ProfileMenuItem>
            </ProfileMenu>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        padding: '24px 32px',
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          padding: '48px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>💳</div>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '12px'
          }}>
            Billing & Usage Dashboard
          </h2>
          <p style={{
            fontSize: '16px',
            color: '#6B7280',
            marginBottom: '32px',
            maxWidth: '600px',
            margin: '0 auto 32px auto'
          }}>
            View your current plan, monitor usage against limits, download invoices, and manage your subscription.
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            marginTop: '32px',
            textAlign: 'left'
          }}>
            <div style={{
              padding: '24px',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>📋</div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '8px'
              }}>
                Current Plan
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#6B7280',
                margin: 0
              }}>
                View your subscription tier, features, and billing cycle
              </p>
            </div>

            <div style={{
              padding: '24px',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>📊</div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '8px'
              }}>
                Usage Limits
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#6B7280',
                margin: 0
              }}>
                Monitor your usage of applications, pages, storage, and API calls
              </p>
            </div>

            <div style={{
              padding: '24px',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🧾</div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '8px'
              }}>
                Invoices
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#6B7280',
                margin: 0
              }}>
                Access and download your billing history and invoices
              </p>
            </div>

            <div style={{
              padding: '24px',
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>💳</div>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                marginBottom: '8px'
              }}>
                Payment Methods
              </h3>
              <p style={{
                fontSize: '14px',
                color: '#6B7280',
                margin: 0
              }}>
                Manage payment methods and update billing information
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;

